import { decode } from '@here/flexpolyline';
import type { Position } from '@rio-cloud/rio-uikit/mapTypes';
import { sumBy } from 'remeda';

const here_api_key = import.meta.env.VITE_MAP_SERVICE_API_KEY;

export type HereRoute = { positions: Position[]; distanceMeters: number; durationSeconds: number };

type HereSection = { polyline: string; summary: { length: number; duration: number } };

const toParam = ({ lat, lng }: Position) => `${lat},${lng}`;

/** Calculates a truck route from the first to the last position, passing through all positions in between. */
export const fetchHereRoute = async (positions: Position[]): Promise<HereRoute> => {
    const origin = positions.at(0);
    const destination = positions.at(-1);
    if (!origin || !destination || positions.length < 2) throw new Error('At least 2 positions are required');

    const params = new URLSearchParams({
        transportMode: 'truck',
        return: 'polyline,summary',
        apiKey: here_api_key,
        origin: toParam(origin),
        destination: toParam(destination),
    });
    for (const via of positions.slice(1, -1)) {
        params.append('via', `${toParam(via)}!passThrough=true`);
    }

    const res = await fetch(`https://router.hereapi.com/v8/routes?${params}`, {
        method: 'POST',
        body: '{}',
        headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) throw new Error(`HERE API error: ${res.status}`);

    const data = await res.json();
    const sections: HereSection[] | undefined = data.routes?.[0]?.sections;
    if (!sections) throw new Error('HERE found no route');

    return {
        // Every section after the first starts with the last point of the previous one
        positions: sections.flatMap((section, i) =>
            (decode(section.polyline).polyline as [number, number][])
                .slice(i === 0 ? 0 : 1)
                .map(([lat, lng]) => ({ lat, lng }))
        ),
        distanceMeters: sumBy(sections, section => section.summary.length),
        durationSeconds: sumBy(sections, section => section.summary.duration),
    };
};
